import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { IInputTask } from 'interfaces/IInputTask';
import { BrowserStorageUtils } from 'utils/BrowserStorageUtils';

// A plan opened for the first time opens with every task expanded when it
// has only a few, so they can all be seen at once; a longer plan opens
// collapsed, as a scannable list.
export const DEFAULT_EXPAND_ALL_MAX_TASKS = 3;

const getDefaultExpandedTaskIds = (tasks: IInputTask[]): string[] =>
  tasks.length > 0 && tasks.length <= DEFAULT_EXPAND_ALL_MAX_TASKS
    ? tasks.map((task) => task.taskId)
    : [];

/**
 * Expanded tasks on the Tasks grid, remembered per plan (OP-2534 AC 18):
 * switching plans restores each plan's own expanded tasks, and a plan opened
 * for the first time gets a default (see DEFAULT_EXPAND_ALL_MAX_TASKS).
 *
 * The default waits until the plan's tasks have loaded, so an empty first
 * render can't fix a plan as "nothing expanded".
 *
 * @param inputId - The plan being shown
 * @param draftTasks - Its tasks, as they load
 */
export const useRememberedExpandedTasks = (
  inputId: string,
  draftTasks: IInputTask[]
): [string[], React.Dispatch<React.SetStateAction<string[]>>] => {
  const [expandedTaskKeys, setExpandedTaskKeys] = useState<string[]>(
    () => BrowserStorageUtils.getExpandedTaskIds(inputId) ?? []
  );
  // Whether this plan's state is settled: restored from storage, defaulted,
  // or changed by the user. Until then nothing is written back.
  const isSettledRef = useRef(
    BrowserStorageUtils.getExpandedTaskIds(inputId) !== null
  );
  const previousInputIdRef = useRef(inputId);

  // Switching plans restores the next plan's state during render, so the
  // grid never shows one plan's tasks with another plan's expanded keys.
  if (previousInputIdRef.current !== inputId) {
    previousInputIdRef.current = inputId;
    const stored = BrowserStorageUtils.getExpandedTaskIds(inputId);
    isSettledRef.current = stored !== null;
    setExpandedTaskKeys(stored ?? []);
  }

  useEffect(() => {
    if (isSettledRef.current || draftTasks.length === 0) return;
    isSettledRef.current = true;
    setExpandedTaskKeys(getDefaultExpandedTaskIds(draftTasks));
  }, [draftTasks]);

  useEffect(() => {
    if (!isSettledRef.current) return;
    BrowserStorageUtils.storeExpandedTaskIds(inputId, expandedTaskKeys);
  }, [inputId, expandedTaskKeys]);

  // Stable, like a plain state setter, so callers' memos don't churn.
  const setAndSettle = useCallback<
    React.Dispatch<React.SetStateAction<string[]>>
  >((action) => {
    isSettledRef.current = true;
    setExpandedTaskKeys(action);
  }, []);

  return [expandedTaskKeys, setAndSettle];
};
